import { number } from 'fp-ts';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { FormCheckboxField, FormSelectField, FormTextareaField, LocalizedText, useFeatureFlag } from '@mews/framework';
import { DropdownItem, Stack, Typography } from '@optimus-web/core';
import { LocalizedSmallLabel } from '@mews-commander/ui';
import { WithEnterpriseIds } from '../../../../../types';
import { LockableField } from '../../LockableField';
import {
    BUSINESS_SEGMENT_KEY_FIELD_NAME,
    DESCRIPTION_FIELD_NAME,
    ENABLED_FIELD_NAME,
    EXTERNAL_NAME_FIELD_NAME,
    HIDE_PRICE_FROM_GUEST_FIELD_NAME,
    IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
    NAME_FIELD_NAME,
    ORDERING_FIELD_NAME,
    RATE_GROUP_KEY_FIELD_NAME,
    RATE_TYPE_FIELD_NAME,
    SHORT_NAME_FIELD_NAME,
} from './fieldNames';
import { LockableRateInputField } from './LockableFields';
import { RateTypeField } from './RateTypeField';
import { RateGroupKeyField } from './rateGroupKey';
import { FieldName, WithFieldsConfig } from './types';
import { getFieldDisabledConfig, getFieldLockedConfig } from './helpers';
import { RateKeyField } from './RateKeyField';
import { BusinessSegmentKeyField } from './businessSegmentKey/BusinessSegmentKeyField';
import { AccountingCategoriesKeyField } from './accountingCategoriesKey/AccountingCategoriesKey';

interface CommonFieldsProps extends WithEnterpriseIds, WithFieldsConfig {
    selectedRateIds?: string[];
    children?: React.ReactNode;
    isDependentRate?: boolean;
    unlockField?: (fieldName: FieldName) => void;
}

export const CommonFields = ({
    enterpriseIds,
    children,
    fieldsConfig,
    selectedRateIds,
    isDependentRate,
    unlockField,
}: CommonFieldsProps) => {
    const nordicChoiceRateOptionsEnabled = useFeatureFlag(FeatureFlag.nordicChoiceRateOptionsEnabled);
    const isCreateForm = !number.isNumber(selectedRateIds?.length) || selectedRateIds?.length === 0;
    const getUnlockFunction = (fieldName: FieldName) => () => (unlockField ? unlockField(fieldName) : null);

    return (
        <>
            <FormSelectField
                name={ENABLED_FIELD_NAME}
                disabled={getFieldDisabledConfig(ENABLED_FIELD_NAME, fieldsConfig)}
                label={<LocalizedText textKey={TextKey.Status} />}
            >
                <DropdownItem value={1} label={<LocalizedText textKey={TextKey.Enabled} />} />
                <DropdownItem value={0} label={<LocalizedText textKey={TextKey.Disabled} />} />
            </FormSelectField>
            <Stack vertical spacing="200">
                <Typography textStyle="bodyLargeStrong" as="div">
                    <LocalizedText textKey={TextKey.InternalDetails} />
                </Typography>

                <LockableRateInputField
                    name={NAME_FIELD_NAME}
                    label={<LocalizedText textKey={TextKey.InternalName} />}
                    fieldsConfig={fieldsConfig}
                    onUnlock={getUnlockFunction(NAME_FIELD_NAME)}
                    required
                />

                <LockableRateInputField
                    name={SHORT_NAME_FIELD_NAME}
                    label={<LocalizedText textKey={TextKey.ShortName} />}
                    fieldsConfig={fieldsConfig}
                    helper={<LocalizedText textKey={TextKey.ShortNameHelperText} />}
                    onUnlock={getUnlockFunction(SHORT_NAME_FIELD_NAME)}
                />
                <RateGroupKeyField
                    enterpriseIds={enterpriseIds}
                    fieldsConfig={fieldsConfig}
                    isDependentRate={isDependentRate}
                    onUnlock={getUnlockFunction(RATE_GROUP_KEY_FIELD_NAME)}
                />
                <AccountingCategoriesKeyField
                    enterpriseIds={enterpriseIds}
                    fieldsConfig={fieldsConfig}
                    isCreateForm={isCreateForm}
                />

                <BusinessSegmentKeyField
                    selectedRateIds={selectedRateIds}
                    enterpriseIds={enterpriseIds}
                    fieldsConfig={fieldsConfig}
                    isCreateForm={isCreateForm}
                    onUnlock={getUnlockFunction(BUSINESS_SEGMENT_KEY_FIELD_NAME)}
                />

                <LockableRateInputField
                    name={ORDERING_FIELD_NAME}
                    label={<LocalizedText textKey={TextKey.Ordering} />}
                    fieldsConfig={fieldsConfig}
                    onUnlock={getUnlockFunction(ORDERING_FIELD_NAME)}
                />
            </Stack>
            <Stack vertical spacing="200">
                <Typography textStyle="bodyLargeStrong" as="div">
                    <LocalizedText textKey={TextKey.GuestFacingDetails} />
                </Typography>

                <Typography textStyle="bodyMedium" as="div">
                    <LocalizedText textKey={TextKey.GuestFacingDetailsDescription} />
                </Typography>

                <LockableRateInputField
                    name={EXTERNAL_NAME_FIELD_NAME}
                    label={<LocalizedText textKey={TextKey.RateName} />}
                    fieldsConfig={fieldsConfig}
                    onUnlock={getUnlockFunction(EXTERNAL_NAME_FIELD_NAME)}
                />
                <LockableField
                    fieldName={DESCRIPTION_FIELD_NAME}
                    locked={getFieldLockedConfig(DESCRIPTION_FIELD_NAME, fieldsConfig)}
                    lockedReasonText={TextKey.RatesValuesAreDifferent}
                    tooltipTextKey={TextKey.UnlockableRatesField}
                    onUnlock={getUnlockFunction(DESCRIPTION_FIELD_NAME)}
                >
                    {(isLocked) => (
                        <FormTextareaField
                            name={DESCRIPTION_FIELD_NAME}
                            label={<LocalizedText textKey={TextKey.Description} />}
                            size="small"
                            disabled={isLocked}
                        />
                    )}
                </LockableField>
            </Stack>
            <Stack vertical spacing="200">
                <Typography textStyle="bodyLargeStrong" as="div">
                    <LocalizedText textKey={TextKey.PricingDetails} />
                </Typography>

                <RateTypeField onUnlock={getUnlockFunction(RATE_TYPE_FIELD_NAME)} fieldsConfig={fieldsConfig} />

                {nordicChoiceRateOptionsEnabled ? (
                    <>
                        <FormCheckboxField
                            name={HIDE_PRICE_FROM_GUEST_FIELD_NAME}
                            disabled={getFieldDisabledConfig(HIDE_PRICE_FROM_GUEST_FIELD_NAME, fieldsConfig)}
                        >
                            <LocalizedText textKey={TextKey.RateBulkHidePriceFromGuest} />
                            <LocalizedSmallLabel
                                variant="secondary"
                                textKey={TextKey.RateOptionsHidePriceFromGuestHint}
                                as="div"
                            />
                        </FormCheckboxField>
                        <FormCheckboxField
                            name={IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME}
                            disabled={getFieldDisabledConfig(IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME, fieldsConfig)}
                        >
                            <LocalizedText textKey={TextKey.RateBulkBonusPointsEligible} />
                            <LocalizedSmallLabel
                                variant="secondary"
                                textKey={TextKey.RateOptionsIsBonusPointsEligibleHint}
                                as="div"
                            />
                        </FormCheckboxField>
                    </>
                ) : null}

                {children}
            </Stack>
            <Stack vertical spacing="200">
                <Typography textStyle="bodyLargeStrong" as="div">
                    <LocalizedText textKey={TextKey.Mapping} />
                </Typography>
                <RateKeyField fieldsConfig={fieldsConfig} />
            </Stack>
        </>
    );
};
